import {
  useDeleteFactorsBranchMutation,
  useGetFactorsBranchesQuery,
} from '@/app/api';
import { useTranslation } from 'react-i18next';
import ComponentWithLoader from '../ComponentWithLoader/ComponentWithLoader';
import { EmptyDataPlaceholder } from '../ui/empty-data-placeholder';
import MapIcon from '@/app/assets/icons/map.svg?react';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { BranchModal } from './BranchModal';
import { BaseModalRef } from '@/app/helpers/types';
import TrashIcon from '@/app/assets/icons/trash.svg?react';
import { checkRole, getFetchEror } from '@/app/helpers';
import { useNotification } from '../contexts/NotificationContext';
import ConfirmDeletion from '../confirm-deletion';
import { useAppSelector } from '@/app/hooks';

export const FactoringBranches = () => {
  const { t } = useTranslation();
  const { profile } = useAppSelector((state) => state.auth);
  const { showNotification } = useNotification();
  const { isAdmin, isHeadOfficeEmployee } = checkRole(profile?.roles);

  const { data: branches, isLoading, refetch } = useGetFactorsBranchesQuery();
  const branchModalRef = useRef<BaseModalRef>(null);
  const [deleteBranch] = useDeleteFactorsBranchMutation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [editingBranchId, setEditingBranchId] = useState<number | null>(null);

  const canAddChangeAndDeleteBranches = isAdmin || isHeadOfficeEmployee;

  const handleDeleteBranch = async (id: number) => {
    try {
      await deleteBranch({ id }).unwrap();
      showNotification(t('settings.branch_successfully_deleted'), 'success');
    } catch (error) {
      showNotification(getFetchEror(error), 'error');
    } finally {
      refetch();
    }
  };

  const askDeleteBranch = (id: number) => {
    setDeletingId(id);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setDeletingId(null);
  };

  const confirmDelete = async () => {
    if (deletingId == null) return;
    await handleDeleteBranch(deletingId);
    closeConfirm();
  };

  const handleAddBranch = () => {
    setEditingBranchId(null);
    branchModalRef.current?.open();
  };

  const handleEditBranch = (branchId: number) => {
    setEditingBranchId(branchId);
    branchModalRef.current?.open();
  };

  const handleModalSuccess = () => {
    setEditingBranchId(null);
    refetch();
  };

  if (branches?.data.length === 0) {
    return (
      <>
        <EmptyDataPlaceholder
          title={t('settings.no_branches')}
          subtitle={t('settings.add_branches')}
          isEmpty={!isLoading && !branches?.data?.length}
          isLoading={isLoading}
          icon={<MapIcon />}
          endAdornment={
            canAddChangeAndDeleteBranches && (
              <Button
                sx={{ fontSize: '1.4rem', fontWeight: 600 }}
                onClick={handleAddBranch}
              >
                {t('settings.add_branch')}
              </Button>
            )
          }
        />
        <BranchModal
          ref={branchModalRef}
          onSuccess={handleModalSuccess}
          editBranchId={editingBranchId}
        />
      </>
    );
  }

  return (
    <>
      <ComponentWithLoader isLoading={isLoading}>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
        >
          <Typography fontSize='1.7rem' fontWeight={600}>
            {t('settings.your_branches')}
          </Typography>
          {canAddChangeAndDeleteBranches && (
            <Button color='inherit' onClick={handleAddBranch}>
              {t('settings.add_branch')}
            </Button>
          )}
        </Stack>
        {branches?.data.map((branch) => (
          <Stack key={branch.unique} direction='column'>
            <Stack spacing={2} mt={2}>
              <Box
                sx={{
                  background: '#0D1D580A',
                  borderRadius: '1.2rem',
                }}
              >
                <Stack
                  direction='row'
                  justifyContent='space-between'
                  alignItems='center'
                  sx={{ padding: 1.2 }}
                >
                  <Typography fontSize='1.7rem' fontWeight={600}>
                    {branch.branchName}
                  </Typography>
                  {canAddChangeAndDeleteBranches && (
                    <Stack direction='row' spacing={2}>
                      <Button
                        sx={{
                          background: '#fff',
                          color: '#0B1370',
                          '&:hover': {
                            background: '#E6F1FF',
                          },
                        }}
                        onClick={() => handleEditBranch(branch.unique)}
                      >
                        {t('settings.edit')}
                      </Button>
                      <Button
                        sx={{
                          minWidth: '4rem',
                          height: '4rem',
                          p: 0,
                          background: '#fff',
                          borderRadius: '0.8rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            background: '#E6F1FF',
                          },
                          '& svg': {
                            width: '1.6rem',
                            height: '1.6rem',
                            '& path': {
                              stroke: 'rgba(13, 29, 88, 0.5)',
                            },
                          },
                        }}
                        onClick={() => askDeleteBranch(branch.unique)}
                      >
                        <TrashIcon />
                      </Button>
                    </Stack>
                  )}
                </Stack>

                <Box sx={{ padding: 1.2, paddingTop: 0 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Box
                        sx={{
                          background: '#fff',
                          borderRadius: '0.8rem',
                          padding: 1.6,
                          height: '88px',
                        }}
                      >
                        <Typography
                          fontSize='1.3rem'
                          fontWeight={600}
                          color='primary.main'
                        >
                          {t('settings.address')}:
                        </Typography>
                        <Typography
                          fontSize='1.4rem'
                          fontWeight={500}
                          color='#0B1370'
                        >
                          {branch.branchAddress}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box
                        sx={{
                          background: '#fff',
                          borderRadius: '0.8rem',
                          padding: 1.6,
                          height: '88px',
                        }}
                      >
                        <Typography
                          fontSize='1.3rem'
                          fontWeight={600}
                          color='primary.main'
                        >
                          {t('settings.managing_director')}:
                        </Typography>
                        <Typography
                          fontSize='1.4rem'
                          fontWeight={500}
                          color='#0B1370'
                        >
                          {branch.managingDirector_cyrl}
                          <br />
                          {branch.managingDirector_latn}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box
                        sx={{
                          background: '#fff',
                          borderRadius: '0.8rem',
                          padding: 1.6,
                          height: '88px',
                        }}
                      >
                        <Typography
                          fontSize='1.3rem'
                          fontWeight={600}
                          color='primary.main'
                        >
                          {t('settings.account')}:
                        </Typography>
                        <Typography
                          fontSize='1.4rem'
                          fontWeight={500}
                          color='#0B1370'
                        >
                          {branch.account}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Stack>
          </Stack>
        ))}
      </ComponentWithLoader>
      <BranchModal
        ref={branchModalRef}
        onSuccess={handleModalSuccess}
        editBranchId={editingBranchId}
      />
      <ConfirmDeletion
        onSubmit={confirmDelete}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        type='branch'
      />
    </>
  );
};
